// Signature cultural SVG pattern tiles — one per language.
// All paint with var(--accent) so they adapt to language + theme automatically.

import React, { useId } from 'react';
import type { Language } from './Scenes.js';

function AzulejoTile({ uid }: { uid: string }) {
  const diamond = "24,4 44,24 24,44 4,24";
  const square  = "10,10 38,10 38,38 10,38";
  return (
    <pattern id={uid} width="48" height="48" patternUnits="userSpaceOnUse">
      <polygon points={diamond} fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
      <polygon points={square}  fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
      <circle cx="24" cy="24" r="3.4" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
      <circle cx="24" cy="24" r="0.9" fill="var(--accent)"/>
      {([[0,0],[48,0],[0,48],[48,48]] as [number,number][]).map(([cx,cy],i)=>(
        <circle key={i} cx={cx} cy={cy} r="6" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
      ))}
    </pattern>
  );
}

function CalcadaTile({ uid }: { uid: string }) {
  const wave = (y: number) => `M0,${y} q12,-13 24,0 t24,0`;
  const rows = [9, 24, 39];
  return (
    <pattern id={uid} width="48" height="48" patternUnits="userSpaceOnUse">
      {rows.map((y,i)=>(
        <path key={i} d={wave(y)} fill="none" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round"/>
      ))}
    </pattern>
  );
}

function NordicTile({ uid }: { uid: string }) {
  return (
    <pattern id={uid} width="56" height="56" patternUnits="userSpaceOnUse">
      <polygon points="28,12 40,28 28,44 16,28" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
      <polygon points="28,20 34,28 28,36 22,28" fill="var(--accent)" opacity="0.25"/>
      <polygon points="2,40 8,28 14,40" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
      <polygon points="42,40 48,28 54,40" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
      <circle cx="0"  cy="28" r="1.4" fill="var(--accent)"/>
      <circle cx="56" cy="28" r="1.4" fill="var(--accent)"/>
      <circle cx="28" cy="0"  r="1.4" fill="var(--accent)"/>
      <circle cx="28" cy="56" r="1.4" fill="var(--accent)"/>
    </pattern>
  );
}

const MOTIF_LABEL: Record<Language, string> = {
  Spanish:    'azulejo · Andalucía',
  Portuguese: 'calçada · Copacabana',
  Finnish:    'kansanperinne · Suomi',
};

function getTile(language: Language): (props: { uid: string }) => React.ReactElement {
  if (language === 'Portuguese') return CalcadaTile;
  if (language === 'Finnish') return NordicTile;
  return AzulejoTile;
}

export function motifLabel(language: Language): string {
  return MOTIF_LABEL[language] ?? MOTIF_LABEL.Spanish;
}

/** Full-width decorative band — used as a section header rule. */
export function MotifBand({
  language,
  height = 56,
  opacity = 0.5,
  fade = false,
}: {
  language: Language;
  height?: number;
  opacity?: number;
  fade?: boolean;
}) {
  const rawId = useId().replace(/:/g, '');
  const uid = rawId + '-band';
  const Tile = getTile(language);
  return (
    <svg width="100%" height={height} preserveAspectRatio="xMidYMid slice"
      style={{ display:'block' }} aria-hidden="true">
      <defs>
        <Tile uid={uid}/>
        {fade && (
          <linearGradient id={uid+'-g'} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="white" stopOpacity="1"/>
            <stop offset="70%" stopColor="white" stopOpacity="0.35"/>
            <stop offset="100%" stopColor="white" stopOpacity="0"/>
          </linearGradient>
        )}
        {fade && (
          <mask id={uid+'-m'}>
            <rect width="100%" height="100%" fill={`url(#${uid}-g)`}/>
          </mask>
        )}
      </defs>
      <rect width="100%" height="100%" fill={`url(#${uid})`} opacity={opacity}
        mask={fade ? `url(#${uid}-m)` : undefined}/>
    </svg>
  );
}

/** Faint full-area watermark — used behind hero / pricing content. */
export function MotifWatermark({
  language,
  opacity = 0.06,
}: {
  language: Language;
  opacity?: number;
}) {
  const rawId = useId().replace(/:/g, '');
  const uid = rawId + '-wm';
  const Tile = getTile(language);
  return (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice"
      style={{ position:'absolute', inset:0, pointerEvents:'none' }} aria-hidden="true">
      <defs><Tile uid={uid}/></defs>
      <rect width="100%" height="100%" fill={`url(#${uid})`} opacity={opacity}/>
    </svg>
  );
}
